import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight, Play } from "lucide-react";
import { useContent } from "@/context/ContentContext";
import { useHeaderTheme } from "@/context/HeaderThemeContext";

const TAGS = ["Strategy", "Migration", "Modernization", "Operations"];
const AUTOPLAY_MS = 6500;

function prefersReduced() {
  return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export default function Hero() {
  const { heroSlides } = useContent();
  const { setTheme } = useHeaderTheme();
  const slides = heroSlides && heroSlides.length ? heroSlides : [];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const timer = useRef(null);
  const reduced = prefersReduced();

  const go = useCallback((i) => setIndex(((i % slides.length) + slides.length) % slides.length), [slides.length]);
  const next = useCallback(() => go(index + 1), [go, index]);
  const prev = useCallback(() => go(index - 1), [go, index]);

  useEffect(() => {
    if (reduced || paused || slides.length <= 1) return;
    timer.current = setTimeout(() => setIndex((i) => (i + 1) % slides.length), AUTOPLAY_MS);
    return () => clearTimeout(timer.current);
  }, [index, paused, reduced, slides.length]);

  // Boxed hero sits below the fixed header over a light page area — keep header dark.
  useEffect(() => { setTheme("dark"); }, [setTheme]);

  if (!slides.length) return null;
  const active = slides[index];
  const accentClass = active.accent === "green" ? "text-cbgreen" : "text-primary";
  const mainClass = active.main === "blue" ? "text-primary" : "text-cbink";
  const isVideo = /video/i.test(active.cta2?.label || "");

  return (
    <section className="bg-white pt-[100px] pb-2 lg:pt-[112px] lg:pb-4">
      <div className="container-cb">
        <div
          className="relative isolate overflow-hidden rounded-3xl border border-border bg-gradient-to-b from-[#eef4fd] via-white to-white shadow-card"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          aria-roledescription="carousel"
          aria-label="Cloud Beaker hero"
          data-testid="hero-slideshow"
        >
      {/* photo backgrounds fill the boxed hero */}
      <div className="absolute inset-0" aria-hidden="true">
        {slides.map((s, i) => (
          <div key={s.id} className="absolute inset-0 transition-opacity duration-[1100ms] ease-out" style={{ opacity: i === index ? 1 : 0 }}>
            <img
              src={s.image}
              alt=""
              className={`h-full w-full object-cover object-center ${i === index && !reduced ? "animate-kenburns" : ""}`}
              loading={i === 0 ? "eager" : "lazy"}
              fetchPriority={i === 0 ? "high" : "auto"}
            />
            {/* left blend so text stays readable over the photo */}
            <div className="absolute inset-0 bg-gradient-to-r from-white via-white/85 to-white/10 md:via-white/70 md:to-transparent lg:via-white/55" />
            {/* top + bottom soft blend into page */}
            <div className="absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-[#eef4fd] to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent" />
          </div>
        ))}
        {/* mobile readability scrim */}
        <div className="absolute inset-0 bg-white/45 lg:hidden" />
      </div>

      {/* content */}
      <div className="relative z-10 flex min-h-[440px] items-center px-6 py-12 sm:min-h-[500px] sm:px-10 lg:min-h-[560px] lg:px-14 lg:py-16">
        <div key={active.id} className="max-w-xl">
          <p className="eyebrow animate-heroText" style={{ animationDelay: "40ms" }}>{active.eyebrow}</p>
          <h1 className="mt-4 animate-heroText font-display text-[40px] font-extrabold leading-[1.04] tracking-tight sm:text-[52px] lg:text-[58px]" style={{ animationDelay: "120ms" }}>
            <span className={mainClass}>{active.line1}</span><br />
            <span className={mainClass}>{active.line2}</span><br />
            <span className={accentClass}>{active.line3}</span>
          </h1>
          <p className="mt-5 max-w-lg animate-heroText text-[16px] leading-relaxed text-cbslate sm:text-[17px]" style={{ animationDelay: "220ms" }}>
            {active.paragraph}
          </p>
          <div className="mt-7 flex flex-wrap items-center gap-3 animate-heroText" style={{ animationDelay: "320ms" }}>
            <Link to={active.cta1.to} className="btn-primary" data-testid="hero-cta-primary">
              {active.cta1.label} <ArrowRight size={16} />
            </Link>
            <Link to={active.cta2.to} className="btn-secondary" data-testid="hero-cta-secondary">
              {isVideo && <Play size={15} className="fill-current" />} {active.cta2.label}
            </Link>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-x-4 gap-y-2 animate-heroText" style={{ animationDelay: "420ms" }}>
            {TAGS.map((t, i) => (
              <React.Fragment key={t}>
                <span className="text-xs font-semibold uppercase tracking-[0.18em] text-cbslate">{t}</span>
                {i < TAGS.length - 1 && <span className="text-border">|</span>}
              </React.Fragment>
            ))}
          </div>

          {/* controls */}
          <div className="mt-9 flex items-center gap-4">
            <div className="flex items-center gap-2.5" role="tablist" aria-label="Choose slide">
              {slides.map((s, i) => (
                <button
                  key={s.id}
                  onClick={() => go(i)}
                  aria-label={`Go to slide ${i + 1}`}
                  aria-selected={i === index}
                  data-testid={`hero-dot-${i}`}
                  className="h-2.5 rounded-full transition-all duration-300"
                  style={{ width: i === index ? 34 : 10, background: i === index ? "var(--cb-blue)" : "#cbd5e1" }}
                />
              ))}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={prev} aria-label="Previous slide" data-testid="hero-prev" className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white/80 text-cbslate backdrop-blur transition-colors hover:border-primary hover:text-primary">
                <ChevronLeft size={17} />
              </button>
              <button onClick={next} aria-label="Next slide" data-testid="hero-next" className="flex h-9 w-9 items-center justify-center rounded-full border border-border bg-white/80 text-cbslate backdrop-blur transition-colors hover:border-primary hover:text-primary">
                <ChevronRight size={17} />
              </button>
            </div>
          </div>
        </div>
      </div>
        </div>
      </div>
    </section>
  );
}
